import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useAppStore } from '@core/store'
import {
  useDetachedWindow,
  type DetachedWindowHandlers,
} from '@core/layout/janela-destacada/useDetachedWindow'
import ViewpointsPanel from '@viewer/viewpoints'

// Renders the viewpoints panel inside a real browser window
// (window.open + React createPortal). The popup shares the page's JS
// context so the zustand store, IndexedDB connection, and live model
// references stay in sync without IPC. CSS is replicated by cloning
// the host head's stylesheets, same trick as BrowserWindowSidebar.
function BrowserWindowViewpoints() {
  const isWindow = useAppStore(
    (s) => s.viewpointsPanelOpen && s.viewpointsPanelMode === 'window',
  )
  const setMode = useAppStore((s) => s.setViewpointsPanelMode)
  // O mecanismo é o partilhado (`useDetachedWindow`): clonagem dos
  // estilos, tema seguido da janela principal, e a janela fecha-se com a
  // principal. Bloqueada ou fechada, volta ao painel fixo.
  const handlers = useMemo<DetachedWindowHandlers>(
    () => ({
      onBlocked: () => {
        setMode('fixed')
        // Sem aviso no ecrã: o modo 'window' fica guardado, e ao reabrir a
        // aplicação o browser bloqueia a janela (não houve gesto). Volta
        // ao painel fixo em silêncio, como antes.
        console.warn('[BrowserWindowViewpoints] janela bloqueada')
      },
      onClose: () => setMode('fixed'),
    }),
    [setMode],
  )
  const { container } = useDetachedWindow(isWindow, handlers, {
    name: 'vn2r-viewpoints',
    title: 'VN2R IFC Viewer · Viewpoints',
    features: 'width=520,height=800,resizable=yes,scrollbars=yes',
  })

  if (!isWindow || !container) return null

  return createPortal(
    <>
      <div className="flex h-8 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs text-[var(--color-text)]">
        <span className="truncate">Viewpoints · VN2R IFC Viewer</span>
        <button
          type="button"
          onClick={() => setMode('fixed')}
          title="Voltar a anexar à barra lateral"
          className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          ↘ anexar
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-[var(--color-bg)]">
        <ViewpointsPanel />
      </div>
    </>,
    container,
  )
}

export default BrowserWindowViewpoints
